import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import CloudOffIcon from '@mui/icons-material/CloudOff'
import WifiOffIcon from '@mui/icons-material/WifiOff'
import SyncIcon from '@mui/icons-material/Sync'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CachedIcon from '@mui/icons-material/Cached'

import { useOfflineMode } from '@/providers/OfflineModeProvider'
import { useServiceWorker } from '@/hooks/useServiceWorker'

export function OfflineStatusIndicator() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const { isOfflineMode, canAccessOfflineMode, timeUntilExpiry } = useOfflineMode()
    const { isSupported: swSupported, isRegistered: swRegistered } = useServiceWorker()

    const handleGoToOfflineMode = () => {
        navigate('/offline/patients')
    }

    // Don't show the banner if we're already on offline pages
    const isOnOfflinePage = location.pathname.startsWith('/offline')

    if (!isOfflineMode && canAccessOfflineMode && !isOnOfflinePage) {
        // Online and can access offline mode - show cache status if available
        // But don't show if already on offline pages
        if (swSupported && swRegistered) {
            return (
                <Alert
                    severity="info"
                    icon={<CachedIcon />}
                    sx={{ mb: 2, backgroundColor: 'success.light' }}
                >
                    <Box sx={{ width: '100%' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {t('App Cached for Offline Use')}
                                </Typography>
                                <Typography variant="body2">
                                    {t('This app will work offline. Your data is automatically synced when online.')}
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleGoToOfflineMode}
                                startIcon={<ArrowForwardIcon />}
                                sx={{
                                    flexShrink: 0,
                                    bgcolor: 'success.main',
                                    '&:hover': {
                                        bgcolor: 'success.dark'
                                    }
                                }}
                            >
                                {t('Offline Mode')}
                            </Button>
                        </Stack>
                    </Box>
                </Alert>
            )
        }
        return null
    }

    if (isOfflineMode && canAccessOfflineMode && !isOnOfflinePage) {
        // Offline but can access offline mode
        // Don't show if already on offline pages
        const hoursRemaining = Math.floor(timeUntilExpiry || 0)
        const minutesRemaining = Math.floor(((timeUntilExpiry || 0) % 1) * 60)

        return (
            <Alert
                severity="info"
                icon={<WifiOffIcon />}
                sx={{ mb: 2 }}
            >
                <Box sx={{ width: '100%' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {t('Offline Mode Active')}
                            </Typography>
                            <Typography variant="body2">
                                {t('You are currently working offline. Changes will sync when connection is restored.')}
                            </Typography>
                            {timeUntilExpiry && timeUntilExpiry > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {t('Offline access expires in')} {hoursRemaining}h {minutesRemaining}m
                                </Typography>
                            )}
                            {swSupported && swRegistered && (
                                <Chip
                                    icon={<CachedIcon />}
                                    label={t('App Cached')}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    sx={{ mt: 1 }}
                                />
                            )}
                        </Box>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleGoToOfflineMode}
                            startIcon={<ArrowForwardIcon />}
                            sx={{
                                flexShrink: 0,
                                bgcolor: 'info.main',
                                '&:hover': {
                                    bgcolor: 'info.dark'
                                }
                            }}
                        >
                            {t('Go to Offline Mode')}
                        </Button>
                    </Stack>
                </Box>
            </Alert>
        )
    }

    if (isOfflineMode && !canAccessOfflineMode) {
        // Offline and cannot access offline mode
        return (
            <Alert
                severity="warning"
                icon={<CloudOffIcon />}
                sx={{ mb: 2 }}
            >
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {t('Limited Offline Access')}
                </Typography>
                <Typography variant="body2">
                    {t('Your offline access has expired or is unavailable. Please reconnect to the internet to continue.')}
                </Typography>
                {swSupported && swRegistered && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {t('App is cached and will work when connection is restored.')}
                    </Typography>
                )}
            </Alert>
        )
    }

    return null
}

export function OfflineModeBadge() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { isOfflineMode, canAccessOfflineMode } = useOfflineMode()

    const handleBadgeClick = () => {
        if (canAccessOfflineMode) {
            navigate('/offline/patients')
        }
    }

    if (!isOfflineMode) return null

    return (
        <Chip
            icon={canAccessOfflineMode ? <WifiOffIcon /> : <CloudOffIcon />}
            label={canAccessOfflineMode ? t('Offline') : t('Limited Access')}
            color={canAccessOfflineMode ? 'info' : 'warning'}
            size="small"
            variant="outlined"
            onClick={canAccessOfflineMode ? handleBadgeClick : undefined}
            sx={{
                cursor: canAccessOfflineMode ? 'pointer' : 'default',
                '&:hover': canAccessOfflineMode ? {
                    bgcolor: 'info.light',
                    opacity: 0.8
                } : {}
            }}
        />
    )
}

export default OfflineStatusIndicator 